// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Campanhas.tsx
//  CRM → Campanhas. Porte do FrmCadCampanha/FrmSegmentacaoPublico/
//  FrmAcompanhamentoCampanha. Disparo de mensagens (WhatsApp/E-mail) ao público.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Send, ListChecks, Radio, RotateCcw } from 'lucide-react';

import { useCampanhas } from '@/features/operacional/hooks/useCampanhas';
import { campanhaService } from '@/features/operacional/services/campanhaService';
import { customerGroupService } from '@/features/pessoas/services/customerGroupService';
import {
  CAMPANHA_CANAIS, CAMPANHA_STATUS, CAMPANHA_ENVIO_STATUS,
} from '@/features/operacional/types';
import type { CampanhaResumo, CriarCampanhaRequest, CampanhaEnvio } from '@/features/operacional/types';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SENT = '__none__';
const STATUS_COR: Record<number, string> = {
  0: 'bg-zinc-500/10 text-zinc-500', 1: 'bg-blue-500/10 text-blue-600',
  2: 'bg-emerald-500/10 text-emerald-600', 3: 'bg-rose-500/10 text-rose-600',
  4: 'bg-amber-500/10 text-amber-600', 5: 'bg-rose-500/10 text-rose-600',
};
const ENVIO_COR: Record<number, string> = {
  0: 'bg-amber-500/10 text-amber-600', 1: 'bg-emerald-500/10 text-emerald-600',
  2: 'bg-rose-500/10 text-rose-600', 3: 'bg-zinc-500/10 text-zinc-500',
};

interface FormState {
  id?: number; nome: string; canal: number; mensagem: string; assunto: string;
  grupoClienteId: number | ''; classificacao: string; apenasAniversariantes: boolean;
}
const FORM_VAZIO: FormState = {
  nome: '', canal: 0, mensagem: '', assunto: '', grupoClienteId: '', classificacao: '', apenasAniversariantes: false,
};

const Campanhas = () => {
  const { query, create, update, remove, disparar, reprocessar } = useCampanhas();
  const { data: grupos = [] } = useQuery({ queryKey: ['grupos-clientes'], queryFn: () => customerGroupService.getAll() });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(FORM_VAZIO);
  const editando = form.id != null;

  const [enviosOpen, setEnviosOpen] = useState(false);
  const [envios, setEnvios] = useState<CampanhaEnvio[]>([]);
  const [enviosCampanha, setEnviosCampanha] = useState<string>('');

  const grupoNome = (id?: number | null) => id ? (grupos.find((g) => g.id === id)?.descricao ?? `#${id}`) : 'Todos';

  const abrirNovo = () => { setForm(FORM_VAZIO); setDialogOpen(true); };
  const abrirEdicao = async (c: CampanhaResumo) => {
    const d = await campanhaService.getById(c.id);
    setForm({
      id: d.id, nome: d.nome, canal: d.canal, mensagem: d.mensagem ?? '', assunto: d.assunto ?? '',
      grupoClienteId: d.grupoClienteId ?? '', classificacao: d.classificacao ?? '',
      apenasAniversariantes: d.apenasAniversariantes,
    });
    setDialogOpen(true);
  };

  const salvar = () => {
    if (!form.nome.trim()) { toast.error('Nome é obrigatório.'); return; }
    const req: CriarCampanhaRequest = {
      nome: form.nome, canal: form.canal,
      mensagem: form.mensagem || null,
      assunto: form.assunto || null,
      grupoClienteId: form.grupoClienteId === '' ? null : Number(form.grupoClienteId),
      classificacao: form.classificacao || null,
      apenasAniversariantes: form.apenasAniversariantes,
    };
    const ok = () => { toast.success(editando ? 'Campanha atualizada.' : 'Campanha criada.'); setDialogOpen(false); };
    const err = (e: unknown) => toast.error(getApiErrorMessage(e));
    if (editando) update.mutate({ id: form.id!, req }, { onSuccess: ok, onError: err });
    else create.mutate(req, { onSuccess: ok, onError: err });
  };

  const preverPublico = async (c: CampanhaResumo) => {
    try {
      const p = await campanhaService.publico(c.id);
      toast.info(`Público estimado: ${p.total} destinatário(s) no canal ${CAMPANHA_CANAIS[c.canal]}.`, {
        description: p.amostra.slice(0, 5).map((i) => `${i.nome} (${i.destino ?? '—'})`).join(', ') || 'Sem amostra.',
      });
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const fazerDisparo = (c: CampanhaResumo) => {
    if (!confirm(`Disparar a campanha "${c.nome}" via ${CAMPANHA_CANAIS[c.canal]}?\nUma mensagem será enviada a cada destinatário do público-alvo.`)) return;
    disparar.mutate(c.id, {
      onSuccess: (r) => toast.success(`Disparo concluído: ${r.enviados} enviado(s), ${r.erros} com erro (total ${r.total}).`),
      onError: (e) => toast.error(getApiErrorMessage(e)),
    });
  };

  const fazerReprocesso = (c: CampanhaResumo) => {
    if (!confirm(`Reprocessar os envios com erro da campanha "${c.nome}"?\nQuem já recebeu NÃO será reenviado.`)) return;
    reprocessar.mutate(c.id, {
      onSuccess: (r) => toast.success(`Reprocessamento: ${r.enviados} reenviado(s), ${r.erros} ainda com erro, ${r.ignorados} ignorado(s).`),
      onError: (e) => toast.error(getApiErrorMessage(e)),
    });
  };

  const verEnvios = async (c: CampanhaResumo) => {
    try {
      const lst = await campanhaService.envios(c.id);
      setEnvios(lst); setEnviosCampanha(c.nome); setEnviosOpen(true);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const excluir = (c: CampanhaResumo) => {
    if (!confirm('Remover esta campanha?')) return;
    remove.mutate(c.id, {
      onSuccess: () => toast.success('Campanha removida.'),
      onError: (e) => toast.error(getApiErrorMessage(e)),
    });
  };

  const campanhas = query.data ?? [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radio className="h-6 w-6" /> Campanhas
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Disparo de mensagens (WhatsApp/E-mail) a um público segmentado de clientes.
          </p>
        </div>
        <Button onClick={abrirNovo} className="gap-2"><Plus className="h-4 w-4" /> Nova Campanha</Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
          ) : campanhas.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma campanha cadastrada.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead className="w-28">Canal</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead className="w-24 text-right">Público</TableHead>
                    <TableHead className="w-64" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campanhas.map((c) => {
                    const rascunho = c.status === 0;
                    const comFalha = c.status === 4 || c.status === 5; // Parcial/Falha → permite retry
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.nome}</TableCell>
                        <TableCell><Badge variant="secondary">{CAMPANHA_CANAIS[c.canal] ?? c.canal}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {grupoNome(c.grupoClienteId)}{c.apenasAniversariantes ? ' · aniversariantes' : ''}
                        </TableCell>
                        <TableCell><Badge variant="secondary" className={STATUS_COR[c.status]}>{CAMPANHA_STATUS[c.status] ?? c.status}</Badge></TableCell>
                        <TableCell className="text-right text-sm">{c.publicoEstimado || '—'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" className="h-8 gap-1" onClick={() => preverPublico(c)} title="Prever público">
                              <Users className="h-3.5 w-3.5" />
                            </Button>
                            {rascunho && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1 text-emerald-600" onClick={() => fazerDisparo(c)} title="Disparar"
                                disabled={disparar.isPending}>
                                <Send className="h-3.5 w-3.5" /> Disparar
                              </Button>
                            )}
                            {!rascunho && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1" onClick={() => verEnvios(c)} title="Acompanhamento">
                                <ListChecks className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {comFalha && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1 text-amber-600" onClick={() => fazerReprocesso(c)} title="Reprocessar os envios com erro"
                                disabled={reprocessar.isPending}>
                                <RotateCcw className="h-3.5 w-3.5" /> Reprocessar
                              </Button>
                            )}
                            {rascunho && (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => abrirEdicao(c)}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => excluir(c)}>
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editando ? 'Editar Campanha' : 'Nova Campanha'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input value={form.nome} maxLength={120} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Canal</Label>
                <Select value={String(form.canal)} onValueChange={(v) => setForm((f) => ({ ...f, canal: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAMPANHA_CANAIS.map((c, i) => <SelectItem key={i} value={String(i)}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Grupo de cliente</Label>
                <Select value={form.grupoClienteId === '' ? SENT : String(form.grupoClienteId)}
                  onValueChange={(v) => setForm((f) => ({ ...f, grupoClienteId: v === SENT ? '' : Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SENT}>Todos os clientes</SelectItem>
                    {grupos.filter((g) => !g.isInativo).map((g) => <SelectItem key={g.id} value={String(g.id)}>{g.descricao}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.canal === 1 && (
              <div className="space-y-1">
                <Label>Assunto (e-mail)</Label>
                <Input value={form.assunto} maxLength={120} onChange={(e) => setForm((f) => ({ ...f, assunto: e.target.value }))} />
              </div>
            )}
            <div className="space-y-1">
              <Label>Mensagem</Label>
              <Textarea value={form.mensagem} rows={5}
                onChange={(e) => setForm((f) => ({ ...f, mensagem: e.target.value }))}
                placeholder="Use {nome} para personalizar com o nome do cliente." />
              <p className="text-xs text-muted-foreground">Dica: <code>{'{nome}'}</code> é substituído pelo nome do cliente.</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="aniver" checked={form.apenasAniversariantes}
                onCheckedChange={(v) => setForm((f) => ({ ...f, apenasAniversariantes: !!v }))} />
              <Label htmlFor="aniver" className="font-normal cursor-pointer">Apenas aniversariantes do dia</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={create.isPending || update.isPending}>{editando ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Envios (acompanhamento) dialog */}
      <Dialog open={enviosOpen} onOpenChange={setEnviosOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Acompanhamento — {enviosCampanha}</DialogTitle></DialogHeader>
          {envios.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nenhum envio registrado.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead>Erro</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {envios.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm">{e.clienteNome ?? `#${e.clienteId}`}</TableCell>
                      <TableCell className="text-sm">{e.destino ?? '—'}</TableCell>
                      <TableCell><Badge variant="secondary" className={ENVIO_COR[e.status]}>{CAMPANHA_ENVIO_STATUS[e.status] ?? e.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.erro ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campanhas;
